import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle';
import { faCircleDashed } from '@fortawesome/pro-solid-svg-icons/faCircleDashed';
import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';

import { FulfillmentPlanOrderGridStatus } from '../../../../enums/fulfillmentplan-order-grid-status';
import { useFulfillmentPlanIdContext } from '../../../../fulfillment-plans/context/fulfillment-plan-id-context-provider';
import { useFulfillmentPlanDeleteStatus } from '../../../../fulfillment-plans/delete-modal/hooks';
import { useEntitiesFulfillmentPlanProperty } from '../../../../hooks/entities/selectors/fulfillment-plans';
import { useModal } from '../../../../hooks/use-modal';
import { goToOrderDetails } from '../../../../store/orders/order-details/orders.details.actions';
import { useSalesOrderIdContext } from '../../../context/sales-order-id-context-provider';
import { ShipmentCounter } from '../../fulfillment-plan';
import messages from '../../fulfillment-plan/shipment-actions/messages';
import DeleteShipmentMenuItemModal from '../../fulfillment-plan/shipment-actions/modals/delete';
import { NavigationButton } from '../navigation-panel/navigation-button';
import { useShowOrderOverview } from '../../hooks/use-show-order-overview';
import {
  ORDER_DETAILS_ACTIONS,
  DELETE_SHIPMENT_ACTIONS,
  useTrackDeleteShipmentAction,
  useTrackOrderDetailsAction,
} from '../../../../orders/modals/order-details-modal/event-tracking/use-track-order-details-action';

import style from './shipments-panel-button.module.less';

const statusToIconMap = {
  [FulfillmentPlanOrderGridStatus.Shipped.name]: {
    icon: faCheckCircle,
    iconClassName: style['shipped-icon'],
  },
};

const defaultIcon = {
  icon: faCircleDashed,
  iconClassName: style['default-icon'],
};

export const ShipmentsPanelButton = ({ fulfillmentPlanId, counter }) => {
  const { isOpen: isConfirmationModalOpen, open: openConfirmationModal, close: closeConfirmationModal } = useModal();

  const salesOrderId = useSalesOrderIdContext();
  const selectedFulfillmentPlanId = useFulfillmentPlanIdContext();
  const showOrderOverview = useShowOrderOverview();

  const isFulfillmentPlanSelected = selectedFulfillmentPlanId && selectedFulfillmentPlanId === fulfillmentPlanId;
  const isSelected = isFulfillmentPlanSelected && !showOrderOverview;

  const dispatch = useDispatch();
  const intl = useIntl();

  const navigateToFulfillmentPlan = useCallback(() => {
    dispatch(
      goToOrderDetails({
        salesOrderId,
        fulfillmentPlanId,
        selectOrderInGrid: true,
      }),
    );
  }, [dispatch, fulfillmentPlanId, salesOrderId]);

  const fulfillmentPlanStatus = useEntitiesFulfillmentPlanProperty(fulfillmentPlanId, 'orderGridStatus');
  const { icon, iconClassName } = statusToIconMap[fulfillmentPlanStatus] || defaultIcon;

  const { canDelete } = useFulfillmentPlanDeleteStatus(fulfillmentPlanId) ?? {};
  const trackDeleteShipmentOpen = useTrackDeleteShipmentAction(DELETE_SHIPMENT_ACTIONS.OPEN, fulfillmentPlanId);
  const trackDeleteShipmentClose = useTrackDeleteShipmentAction(DELETE_SHIPMENT_ACTIONS.CLOSE, fulfillmentPlanId);
  const trackOrderDetailsAction = useTrackOrderDetailsAction(ORDER_DETAILS_ACTIONS.DELETE_SHIPMENT);

  const onOpenConfirmationModal = useCallback(
    (e) => {
      e.stopPropagation();
      openConfirmationModal();
      trackOrderDetailsAction();
      trackDeleteShipmentOpen();
    },
    [openConfirmationModal, trackDeleteShipmentOpen, trackOrderDetailsAction],
  );

  const onCloseConfirmationModal = useCallback(() => {
    closeConfirmationModal();
    trackDeleteShipmentClose();
  }, [closeConfirmationModal, trackDeleteShipmentClose]);

  return (
    <NavigationButton icon={icon} iconClassName={iconClassName} isSelected={isSelected} onClick={navigateToFulfillmentPlan}>
      <div className={style['button-content']}>
        <ShipmentCounter counter={counter} />
        {isSelected && canDelete && (
          <>
            <Button
              ariaLabel={intl.formatMessage(messages.deleteShipment)}
              className={style['delete-button']}
              onClick={onOpenConfirmationModal}
              theme='unstyled'
            >
              <FontAwesomeIcon className={style['delete-button-icon']} icon={faTrash} />
            </Button>
            {isConfirmationModalOpen && <DeleteShipmentMenuItemModal onClose={onCloseConfirmationModal} />}
          </>
        )}
      </div>
    </NavigationButton>
  );
};

ShipmentsPanelButton.propTypes = {
  counter: PropTypes.number.isRequired,
  fulfillmentPlanId: PropTypes.string.isRequired,
};
